// ==============================================
// ModelForm.jsx
// Form for adding or editing AI model configurations
// ==============================================
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { X, Save, PlusCircle, Trash2, ArrowRight } from 'lucide-react';

const PLATFORM_OPTIONS = ["OPENAI", "GEMINI", "CLAUDE", "MISTRAL", "OPENROUTER", "GROK", "OTHER"];

const INITIAL_MODEL_DETAILS = {
    acceptsAudio: false,
    acceptsImages: false,
    // acceptsTemperature: true, // Decided to infer from temperatureRange or make it advanced
    cleanModelName: '',
    hasTools: false,
    maxTokens: 4096,
    maxContextWindow: null, // Optional
    order: 99,
    systemMessageRider: null,
    temperatureRange: [0.0, 1.0],
    tokenPricing: [0.0, 0.0],
    category: 'general', // Added category
};

const ModelForm = ({ modelData, isNew, onSubmit, onCancel, allModelsDoc }) => {
    const [formData, setFormData] = useState(INITIAL_MODEL_DETAILS);
    const [platform, setPlatform] = useState('');
    const [modelId, setModelId] = useState(''); // The key for the model, e.g. "gpt-4o-mini"
    const [isCustomPlatform, setIsCustomPlatform] = useState(false);

    const existingPlatforms = useMemo(() => {
        return allModelsDoc?.models ? Object.keys(allModelsDoc.models) : [];
    }, [allModelsDoc]);

    useEffect(() => {
        if (modelData && !isNew) { // Editing existing model
            setPlatform(modelData.platformName);
            setModelId(modelData.modelId);
            // Ensure all fields from INITIAL_MODEL_DETAILS are present, falling back to defaults
            const mergedDetails = {
                ...INITIAL_MODEL_DETAILS,
                ...modelData.modelDetails,
                // Ensure arrays have correct length if they exist, otherwise use default
                temperatureRange: Array.isArray(modelData.modelDetails?.temperatureRange) && modelData.modelDetails.temperatureRange.length === 2
                    ? modelData.modelDetails.temperatureRange
                    : INITIAL_MODEL_DETAILS.temperatureRange,
                tokenPricing: Array.isArray(modelData.modelDetails?.tokenPricing) && modelData.modelDetails.tokenPricing.length === 2
                    ? modelData.modelDetails.tokenPricing
                    : INITIAL_MODEL_DETAILS.tokenPricing,
                maxContextWindow: modelData.modelDetails?.maxContextWindow ?? null,
                systemMessageRider: modelData.modelDetails?.systemMessageRider ?? null,

            };
            setFormData(mergedDetails);
            setIsCustomPlatform(!PLATFORM_OPTIONS.includes(modelData.platformName.toUpperCase()));
        } else if (isNew && modelData?.platformName) { // Adding new model to a specific platform
             setPlatform(modelData.platformName);
             setModelId('');
             setFormData(INITIAL_MODEL_DETAILS);
             setIsCustomPlatform(!PLATFORM_OPTIONS.includes(modelData.platformName.toUpperCase()));
        }
        else { // Adding completely new model (platform might be new too)
            setPlatform('');
            setModelId('');
            setFormData(INITIAL_MODEL_DETAILS);
            setIsCustomPlatform(false);
        }
    }, [modelData, isNew]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value)
        }));
    };

    const handleArrayChange = (name, index, value) => {
        setFormData(prev => {
            const newArray = [...prev[name]];
            newArray[index] = parseFloat(value) || 0;
            return { ...prev, [name]: newArray };
        });
    };
    
    const handleNullableNumberChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value === '' ? null : parseFloat(value)
        }));
    };

    const handlePlatformChange = (e) => {
        const selectedPlatform = e.target.value;
        if (selectedPlatform === "OTHER") {
            setIsCustomPlatform(true);
            setPlatform(""); // Clear platform so user can type
        } else {
            setIsCustomPlatform(false);
            setPlatform(selectedPlatform);
        }
    };
    
    const handleCustomPlatformNameChange = (e) => {
        setPlatform(e.target.value.toUpperCase());
    };


    const handleSubmit = (e) => {
        e.preventDefault();
        if (!platform.trim()) {
            toast.error("Platform name cannot be empty.");
            return;
        }
        if (!modelId.trim()) {
            toast.error("Model ID cannot be empty.");
            return;
        }
        // Basic validation for ranges
        if (formData.temperatureRange[0] > formData.temperatureRange[1]) {
            toast.error("Min temperature cannot be greater than max temperature.");
            return;
        }
        // Prevent overwriting an existing model with a different ID during "add new"
        if (isNew && allModelsDoc?.models?.[platform.toUpperCase()]?.[modelId.trim()]) {
            toast.error(`Model ID "${modelId.trim()}" already exists for platform "${platform.toUpperCase()}". Please use a unique ID.`);
            return;
        }

        const finalSystemMessageRider = formData.systemMessageRider === '' ? null : formData.systemMessageRider;
        const finalMaxContextWindow = formData.maxContextWindow === '' || isNaN(parseFloat(formData.maxContextWindow)) ? null : parseFloat(formData.maxContextWindow);


        onSubmit({
            platformName: platform.toUpperCase().trim(),
            modelId: modelId.trim(),
            modelDetails: {
                ...formData,
                systemMessageRider: finalSystemMessageRider,
                maxContextWindow: finalMaxContextWindow,
                // Ensure numbers are numbers
                maxTokens: parseFloat(formData.maxTokens),
                order: parseInt(formData.order, 10),
            }
        });
    };

    const title = isNew ? (modelData?.platformName ? `Add New Model to ${modelData.platformName}` : "Add New AI Model") : `Edit Model: ${modelData?.modelDetails?.cleanModelName || modelId}`;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border-color">
                <div className="flex items-center justify-between p-6 border-b border-border-color">
                    <h2 className="text-xl font-semibold">{title}</h2>
                    <button onClick={onCancel} className="p-2 rounded-full hover:bg-surface-dark">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="overflow-y-auto flex-grow">
                    <div className="p-6 space-y-6">
                        {/* Platform and Model ID */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="platform" className="block text-sm font-medium mb-1">Platform</label>
                                <select
                                    id="platform"
                                    value={isCustomPlatform ? "OTHER" : platform.toUpperCase()}
                                    onChange={handlePlatformChange}
                                    disabled={!isNew && !isCustomPlatform} // Can't change platform easily when editing unless it was custom
                                    className="w-full px-4 py-2.5 bg-surface-dark rounded-lg border border-border-color focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                >
                                    <option value="" disabled>Select Platform</option>
                                    {PLATFORM_OPTIONS.filter(p => p !== "OTHER").map(p => <option key={p} value={p}>{p}</option>)}
                                    {existingPlatforms.filter(p => !PLATFORM_OPTIONS.includes(p.toUpperCase())).map(p => <option key={p} value={p.toUpperCase()}>{p.toUpperCase()}</option>)}
                                    <option value="OTHER">Other (Specify)</option>
                                </select>
                                {isCustomPlatform && (
                                    <input
                                        type="text"
                                        placeholder="Enter Custom Platform Name (e.g., MY_COMPANY_AI)"
                                        value={platform}
                                        onChange={handleCustomPlatformNameChange}
                                        disabled={!isNew}
                                        className="mt-2 w-full px-4 py-2.5 bg-surface-dark rounded-lg border border-border-color focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                )}
                            </div>
                            <div>
                                <label htmlFor="modelId" className="block text-sm font-medium mb-1">Model ID (Unique Key)</label>
                                <input
                                    type="text"
                                    id="modelId"
                                    name="modelId"
                                    placeholder="e.g., gpt-4o-mini-2024-07-15"
                                    value={modelId}
                                    onChange={(e) => setModelId(e.target.value)}
                                    disabled={!isNew} // Model ID is the key, should not be changed for existing models
                                    required
                                    className="w-full px-4 py-2.5 bg-surface-dark rounded-lg border border-border-color focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                />
                            </div>
                        </div>
                        
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="cleanModelName" className="block text-sm font-medium mb-1">Display Name</label>
                                <input id="cleanModelName" name="cleanModelName" type="text" value={formData.cleanModelName} onChange={handleChange} required className="w-full input-style" placeholder="e.g., GPT-4o Mini"/>
                            </div>
                            <div>
                                <label htmlFor="category" className="block text-sm font-medium mb-1">Category</label>
                                <select id="category" name="category" value={formData.category} onChange={handleChange} className="w-full input-style">
                                    <option value="general">General</option>
                                    <option value="coding">Coding</option>
                                    <option value="creative">Creative</option>
                                    <option value="analysis">Analysis</option>
                                    <option value="research">Research</option>
                                    <option value="image_generation">Image Generation</option>
                                    <option value="experimental">Experimental</option>
                                </select>
                            </div>
                        </div>

                        {/* Capabilities */}
                        <fieldset className="border border-border-color p-4 rounded-lg">
                            <legend className="text-sm font-medium px-2">Capabilities</legend>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input type="checkbox" name="acceptsImages" checked={formData.acceptsImages} onChange={handleChange} className="checkbox-style"/>
                                    <span>Accepts Images</span>
                                </label>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input type="checkbox" name="acceptsAudio" checked={formData.acceptsAudio} onChange={handleChange} className="checkbox-style"/>
                                    <span>Accepts Audio</span>
                                </label>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input type="checkbox" name="hasTools" checked={formData.hasTools} onChange={handleChange} className="checkbox-style"/>
                                    <span>Has Tools</span>
                                </label>
                            </div>
                        </fieldset>

                        {/* Token and Order Configuration */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label htmlFor="maxTokens" className="block text-sm font-medium mb-1">Max Output Tokens</label>
                                <input id="maxTokens" name="maxTokens" type="number" value={formData.maxTokens} onChange={handleChange} className="w-full input-style" placeholder="e.g., 4096"/>
                            </div>
                            <div>
                                <label htmlFor="maxContextWindow" className="block text-sm font-medium mb-1">Max Context Window <span className="text-xs text-on-surface-dim">(Optional)</span></label>
                                <input id="maxContextWindow" name="maxContextWindow" type="number" value={formData.maxContextWindow ?? ''} onChange={handleNullableNumberChange} className="w-full input-style" placeholder="e.g., 128000"/>
                            </div>
                            <div>
                                <label htmlFor="order" className="block text-sm font-medium mb-1">Display Order</label>
                                <input id="order" name="order" type="number" value={formData.order} onChange={handleChange} className="w-full input-style" placeholder="e.g., 10"/>
                            </div>
                        </div>

                        {/* Temperature Range */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Temperature Range (Min <ArrowRight className="inline w-3 h-3"/> Max)</label>
                            <div className="flex items-center gap-4">
                                <input type="number" name="temperatureRangeMin" value={formData.temperatureRange[0]} onChange={(e) => handleArrayChange('temperatureRange', 0, e.target.value)} step="0.01" className="w-full input-style" placeholder="e.g., 0.0"/>
                                <input type="number" name="temperatureRangeMax" value={formData.temperatureRange[1]} onChange={(e) => handleArrayChange('temperatureRange', 1, e.target.value)} step="0.01" className="w-full input-style" placeholder="e.g., 1.0"/>
                            </div>
                        </div>

                        {/* Token Pricing */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Token Pricing (Per 1M tokens: Input <ArrowRight className="inline w-3 h-3"/> Output)</label>
                            <div className="flex items-center gap-4">
                                <input type="number" name="tokenPricingInput" value={formData.tokenPricing[0]} onChange={(e) => handleArrayChange('tokenPricing', 0, e.target.value)} step="0.0001" className="w-full input-style" placeholder="e.g., 0.50"/>
                                <input type="number" name="tokenPricingOutput" value={formData.tokenPricing[1]} onChange={(e) => handleArrayChange('tokenPricing', 1, e.target.value)} step="0.0001" className="w-full input-style" placeholder="e.g., 1.50"/>
                            </div>
                        </div>
                        
                        {/* System Message Rider */}
                        <div>
                            <label htmlFor="systemMessageRider" className="block text-sm font-medium mb-1">System Message Rider <span className="text-xs text-on-surface-dim">(Optional)</span></label>
                            <textarea
                                id="systemMessageRider"
                                name="systemMessageRider"
                                value={formData.systemMessageRider ?? ''}
                                onChange={handleChange}
                                rows="3"
                                className="w-full input-style"
                                placeholder="Enter any additional system message content to append..."
                            />
                        </div>
                    </div>
                </form>
                
                <div className="p-6 border-t border-border-color bg-surface-dark/50 flex justify-end space-x-3">
                    <button 
                        type="button" 
                        onClick={onCancel} 
                        className="px-5 py-2.5 rounded-lg border border-border-color hover:bg-surface transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" // Will be caught by the form's onSubmit
                        onClick={handleSubmit} // Also call directly to ensure it runs
                        className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
                    >
                        <Save className="w-4 h-4"/>
                        <span>{isNew ? 'Add Model' : 'Save Changes'}</span>
                    </button>
                </div>
            </div>
            {/* Helper for common input styles - define in your global CSS or a style tag if needed */}
            <style jsx>{`
                .input-style {
                    background-color: var(--color-surface-dark);
                    border: 1px solid var(--color-border-color);
                    border-radius: 0.5rem; /* lg */
                    padding: 0.625rem 1rem; /* 10px 16px */
                    width: 100%;
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                .input-style:focus {
                    outline: none;
                    border-color: var(--color-primary);
                    box-shadow: 0 0 0 2px var(--color-primary-transparent); /* ring-2 primary/20 */
                }
                .checkbox-style {
                    border-radius: 0.25rem; /* rounded */
                    border-color: var(--color-border-color);
                    background-color: var(--color-surface-dark);
                    color: var(--color-primary); /* text-primary */
                }
                .checkbox-style:focus {
                    ring: 2px var(--color-primary-transparent); /* focus:ring-primary/20 */
                }
            `}</style>
        </div>
    );
};

export default ModelForm;
